/**
 * PiiRedactionControls
 * ====================
 *
 * Shared PII-redaction UI — a labelled toggle, replacement-style
 * selector, and category picker. Rendered in three places:
 *
 *   - DocumentUploadTab (tab-level, non-conversational documents)
 *   - FileUpload (tab-level, for Text / Audio / Video tabs)
 *
 * Backend contract for the calling site: send
 *   { redact_pii: true, redaction_substitution: <'entity_name'|'hash'>,
 *     redaction_policy_names: [<policy_value>, ...] }
 * when redactPii is true, omit otherwise. Field names/values are
 * unchanged from before this component existed — no backend impact.
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { TRANSCRIPT_REDACTION_POLICY_OPTIONS } from '@constants/transcriptWorkflows';

const DEFAULT_DESCRIPTION = 'Automatically remove personal information during transcription.';

export const PiiRedactionControls = ({
  redactPii,
  onRedactPiiChange,
  redactionSubstitution,
  onSubstitutionChange,
  redactionPolicies,
  onTogglePolicy,
  disabled = false,
  description = DEFAULT_DESCRIPTION,
  variant = 'plain', // 'plain' | 'card' — card adds a bordered wrapper
}) => {
  const wrapperSx =
    variant === 'card'
      ? {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'grey.50',
        }
      : {};

  return (
    <Box sx={wrapperSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            Redact PII
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Switch
          checked={redactPii}
          onChange={(e) => onRedactPiiChange(e.target.checked)}
          size="small"
          disabled={disabled}
        />
      </Box>

      {redactPii && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Replacement style
          </Typography>
          <ToggleButtonGroup
            value={redactionSubstitution}
            exclusive
            onChange={(_, val) => val && onSubstitutionChange(val)}
            size="small"
            fullWidth
            disabled={disabled}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="entity_name" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Labels{' '}
              <Chip
                label="[PERSON_NAME]"
                size="small"
                sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
              />
            </ToggleButton>
            <ToggleButton value="hash" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Masked{' '}
              <Chip label="####" size="small" sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }} />
            </ToggleButton>
          </ToggleButtonGroup>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              bgcolor: 'background.paper',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
              <Typography variant="caption">
                PII categories ({redactionPolicies.length} selected)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, maxHeight: 200, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {TRANSCRIPT_REDACTION_POLICY_OPTIONS.map((policy) => (
                  <FormControlLabel
                    key={policy.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={redactionPolicies.includes(policy.value)}
                        onChange={() => onTogglePolicy(policy.value)}
                        disabled={disabled}
                      />
                    }
                    label={<Typography variant="caption">{policy.label}</Typography>}
                    sx={{ width: '48%', mr: 0, ml: 0 }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default PiiRedactionControls;
